import { EntityStatus, getStatusForId as getStatus } from '@ms/yammer-data-support';

import { FeatureRollout, Group, Groups, YammerState } from '../../state/types';
import { isAnswersCommunityForAllEnabled } from '../answersCommunity/selectors';
import { getExperimentTreatment } from '../experiments/selectors';
import { isFeatureRolloutEnabled } from '../featureRollouts/selectors';
import { createFindById, createFindByIdThrows } from '../findById';
import { getCurrentSessionUser } from '../session/selectors';
import { getCurrentUser } from '../users/selectors';
import { isAdmin as isGroupAdmin } from '../viewerCommunityRelationships/selectors';

export const AllCompanyGroupId = 'eyJfdHlwZSI6Ikdyb3VwIiwiaWQiOiIwIn0';

export const findById = createFindById('groups');
export const findByIdThrows = createFindByIdThrows('groups');

type GroupBooleanSelector = (state: YammerState, groupId: Group['id']) => boolean;

type GetStatusForId = (state: YammerState, groupId: Group['id']) => EntityStatus;
export const getStatusForId: GetStatusForId = (state, groupId) => getStatus(state.groups, groupId);

type IsFulfilled = (state: YammerState, groupId: Group['id']) => boolean;
export const isFulfilled: IsFulfilled = (state, groupId) => getStatus(state.groups, groupId) === 'fulfilled';

export const isExternal: GroupBooleanSelector = (state, groupId) => {
  const group = findById(state, groupId);

  return !!(group && group.isExternal);
};

export const isMultiTenantOrganizationGroup: GroupBooleanSelector = (state, groupId) => {
  const group = findById(state, groupId);

  return group?.isMultiTenantOrganizationGroup ?? false;
};

export const isOfficial: GroupBooleanSelector = (state, groupId) => {
  const group = findById(state, groupId);

  return group?.isOfficial ?? false;
};

export const isLegacyAllCompany: GroupBooleanSelector = (state, groupId) => {
  const group = findById(state, groupId);

  return !!(group && group.id === AllCompanyGroupId);
};

export const isConnected: GroupBooleanSelector = (state, groupId) => {
  const group = findById(state, groupId);

  return group ? !!group.officeUnifiedGroupId : false;
};

type IsForeignNetworkGroup = (state: YammerState, groupId: Group['id']) => boolean;
export const isForeignNetworkGroup: IsForeignNetworkGroup = (state, groupId) => {
  const group = findById(state, groupId);
  const { networkId } = getCurrentUser(state);

  return group ? group.networkId !== networkId : false;
};

type GetGroupsState = (state: YammerState) => Groups;
export const getGroupsState: GetGroupsState = (state) => state.groups;

type GetFulfilledGroups = (state: YammerState) => Record<string, Group>;
export const getFulfilledGroups: GetFulfilledGroups = (state) => state.groups.fulfilled.byId;

type GetFulfilledGroup = (state: YammerState, groupId?: Group['id']) => Group | undefined;
export const getFulfilledGroup: GetFulfilledGroup = (state, groupId) => {
  if (!groupId) {
    return;
  }

  return findById(state, groupId);
};

export const canEditSettings: GroupBooleanSelector = (state, groupId) => {
  const { isVerifiedNetworkAdmin } = getCurrentSessionUser(state);
  if (isVerifiedNetworkAdmin) {
    return true;
  }

  return isGroupAdmin(state, groupId);
};

export const canCreateEvent: GroupBooleanSelector = (state, groupId) => {
  if (isLegacyAllCompany(state, groupId)) {
    return false;
  }
  const isAdmin = isGroupAdmin(state, groupId);
  const isGroupConnected = isConnected(state, groupId);
  const isCreateFeatureEnabled = isFeatureRolloutEnabled(state, FeatureRollout.EventsCreation);

  return isCreateFeatureEnabled && isAdmin && isGroupConnected;
};

type GetGroupDatabaseId = (state: YammerState, groupId: Group['id']) => string;
export const getGroupDatabaseId: GetGroupDatabaseId = (state, groupId) => findByIdThrows(state, groupId).databaseId;

export const hasGuestsInGroup: GroupBooleanSelector = (state, groupId) => {
  const group = findById(state, groupId);

  return !!(group && group.guestsCount > 0);
};

type GetGroupTelemetryId = (state: YammerState, groupId?: Group['id']) => string | undefined;
export const getGroupTelemetryId: GetGroupTelemetryId = (state, groupId) =>
  groupId ? findById(state, groupId)?.telemetryId : undefined;

type IsMoveThreadToThisGroupRestricted = (state: YammerState, groupId: Group['id'] | null | undefined) => boolean;
export const isMoveThreadToThisGroupRestricted: IsMoveThreadToThisGroupRestricted = (state, groupId) => {
  if (!groupId) {
    return false;
  }

  const group = findById(state, groupId);
  if (!group) {
    return false;
  }

  return group.isMoveThreadToThisGroupRestricted;
};

type IsNetworkQuestionGroup = (state: YammerState, groupId: Group['id'] | null | undefined) => boolean;
export const isNetworkQuestionGroup: IsNetworkQuestionGroup = (state, groupId) => {
  if (!groupId) {
    return false;
  }

  if (isAnswersCommunityForAllEnabled(state)) {
    return true;
  }

  return findById(state, groupId)?.isNetworkQuestionGroup || false;
};

interface GroupTelemetryEventProperties {
  readonly groupId?: Group['telemetryId'];
  readonly isAnswersCommunity?: boolean;
}

type GetGroupTelemetryEventProperties = (
  state: YammerState,
  groupId: Group['id'] | undefined
) => GroupTelemetryEventProperties;
export const getGroupTelemetryEventProperties: GetGroupTelemetryEventProperties = (state, groupId) => {
  if (!groupId) {
    return {};
  }

  return {
    groupId: getGroupTelemetryId(state, groupId),
    isAnswersCommunity: isNetworkQuestionGroup(state, groupId),
  };
};

type ShouldShowQuestionThreadUpdate = (state: YammerState, groupId: Group['id'] | null | undefined) => boolean;
export const shouldShowQuestionThreadUpdate: ShouldShowQuestionThreadUpdate = (state, groupId) => {
  const isQuestionTitlesEnabled = !!getExperimentTreatment(state, 'QuestionTitles');

  return isNetworkQuestionGroup(state, groupId) || isQuestionTitlesEnabled;
};
